import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  useAuthentication,
  useEditAllocation,
  useEditTask,
} from "@planzone/view-models";
import { ConstraintsPopup } from "@common/constraintsPopup";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { TaskStatus } from "./taskStatus";
import { isTaskDone } from "./utils";
import { Props } from "./types";
import styles from "./styles.scss";
import { allocation } from "@planzone/dto";
import { TaskCompletionStatus } from "@common/taskCompletionStatus";
import { isBlocked } from "./utils";

function Task({ task, constraints }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const push = useNavigate();
  const { editTask } = useEditTask(task);
  const { account } = useAuthentication();
  const { editAllocation } = useEditAllocation({ ...allocation, uuid: "" });
  const now = new Date();
  const isLate =
    !!task.endDate &&
    new Date(task.endDate) <
      new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const isDone =
    task.percentComplete === 100 || task.status.percentComplete === 100;
  const userAllocation = task?.allocations?.find(
    (alloc) => alloc.resource.uuid === account.resource.uuid,
  );
  const elementConstraints = constraints.filter(
    ({ targetEntityUuid, originEntityUuid }) =>
      targetEntityUuid === task.uuid || originEntityUuid === task.uuid,
  );
  const isConstrained = elementConstraints.length > 0;
  const blocked = isBlocked(task, elementConstraints);

  return (
    <div
      className={classNames(styles.task, {
        [styles.late]: isLate && !isTaskDone(task),
        [styles.constrained]: blocked,
      })}
    >
      <div
        className={styles.wrapper}
        onClick={() =>
          pushView({
            key: UseCases.retrieveTask,
            data: { task, isMultiProject: true },
          })
        }
      >
        {" "}
        <div className={styles.name}>
          <div
            className={styles.taskCompletionStatus}
            onClick={
              !task.list.project.isTemplate && !blocked
                ? (e) => {
                    e.stopPropagation();
                    userAllocation && !isDone
                      ? task.allocations.length === 1
                        ? pushView({
                            key: UseCases.markTaskAsCompleted,
                            data: {
                              task,
                              project: task.list.project,
                            },
                          })
                        : editAllocation({
                            allocation: userAllocation,
                            nextAllocation: {
                              ...userAllocation,
                              percentComplete:
                                userAllocation.percentComplete !== 100
                                  ? 100
                                  : 0,
                            },
                          })
                      : !isDone
                        ? pushView({
                            key: UseCases.markTaskAsCompleted,
                            data: {
                              task,
                              project: task.list.project,
                            },
                          })
                        : editTask({
                            task,
                            nextTask: { ...task, percentComplete: 0 },
                          });
                  }
                : undefined
            }
          >
            <TaskCompletionStatus
              progress={task.percentComplete}
              endDate={task.endDate}
              resource={account.resource}
              isDisabled={task.list.project.isTemplate}
              isConstrained={blocked}
              task={task}
            />
          </div>
          {task.name}
        </div>
        {isConstrained && (
          <div
            className={styles.constraintsPopup}
            onClick={(e) => e.stopPropagation()}
          >
            <ConstraintsPopup
              elementId={task.uuid}
              elementType="task"
              constraintsNbr={elementConstraints.length}
              project={task.list.project}
            />
          </div>
        )}
        <div className={styles.breadcrumbs}>
          <p
            className={styles.breadcrumb}
            onClick={(e) => {
              e.stopPropagation();
              push(`/projects/${task.list.project.uuid}`);
            }}
          >
            {task.list.project.name}
          </p>
        </div>
      </div>
      <div className={styles.taskStatus}>
        <TaskStatus
          status={task.status}
          project={task.list.project}
          selectStatus={(status) =>
            status.percentComplete === 100
              ? pushView({
                  key: UseCases.editTaskStatus,
                  data: {
                    task,
                    status,
                    project: task.list.project,
                  },
                })
              : editTask({
                  task,
                  nextTask: {
                    ...task,
                    status,
                  },
                })
          }
          isReduced
        />
      </div>
      {task.endDate ? (
        <p className={styles.endDate}>
          {new Date(task.endDate).toLocaleDateString()}
        </p>
      ) : (
        <p className={styles.endDate} />
      )}
    </div>
  );
}

export { Task };
